import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, Loader2, X, Upload, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/I18nContext";
import { api } from "@/lib/api";
import { useToggleSticker, useInventory } from "@/lib/inventory";

/**
 * Phase 21 — FiguScan.
 *
 * Client-side OCR sticker scanner. Tesseract.js is lazy-loaded via dynamic
 * `import()` so its ~2-5 MB wasm+worker chunks only download after the modal
 * opens (never on cold PWA load).
 *
 * Privacy contract (see also Task E in the Phase 21 brief):
 *   • Camera stream is torn down on unmount + on Cancel + after Capture.
 *   • Captured image never leaves the device — no `FormData`, no `Blob`
 *     POST, no dataURL uploaded. Only the extracted integer + user-picked
 *     grade + target list are sent to `/api/inventory/me/toggle`.
 *   • On modal close, all image state (canvas, video srcObject) is dropped.
 */

const GRADES = ["M", "NM", "EX", "G", "F", "HW"];

const cardCx =
  "w-full max-w-[520px] max-h-[92vh] bg-[#0A0A0A] border border-white/10 rounded-xl shadow-[0_24px_64px_-24px_rgba(0,0,0,0.9)] flex flex-col overflow-hidden";

const primaryBtnCx =
  "h-11 px-5 rounded-md font-semibold text-sm tracking-wide " +
  "bg-gradient-to-r from-[#E5B83A] to-[#C9971C] text-black " +
  "hover:opacity-95 active:scale-[0.99] transition-all " +
  "disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2";

const secondaryBtnCx =
  "h-11 px-4 rounded-md font-medium text-sm tracking-wide " +
  "bg-transparent border border-white/15 text-white " +
  "hover:border-white/40 active:scale-[0.99] transition-all " +
  "disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2";

const inputCx =
  "w-full h-11 bg-[#121212] border border-white/10 rounded-md px-3 text-white placeholder:text-white/30 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-[#E5B83A] focus:border-[#E5B83A] transition-colors";

export default function FiguScanModal({ open, onOpenChange }) {
  const { t } = useI18n();
  const toggle = useToggleSticker();
  const { data: inv } = useInventory();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  // Stage machine: camera → analyzing → confirm  (upload fallback swaps
  // camera→confirm directly since we OCR the picked file straight away).
  const [stage, setStage] = useState("camera");
  const [cameraError, setCameraError] = useState("");
  const [ocrError, setOcrError] = useState("");
  const [number, setNumber] = useState("");
  const [grade, setGrade] = useState("NM");
  const [target, setTarget] = useState("duplicates");
  const [submitting, setSubmitting] = useState(false);

  // ── Camera lifecycle ─────────────────────────────────────────────
  const stopStream = useCallback(() => {
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (_) {
          /* autoplay may be blocked; user gesture required */
        }
      }
    } catch (err) {
      setCameraError(err?.name || "PermissionDenied");
    }
  }, []);

  // Open → start camera; close → tear everything down.
  useEffect(() => {
    if (!open) return undefined;
    setStage("camera");
    setNumber("");
    setGrade("NM");
    setTarget("duplicates");
    setOcrError("");
    startCamera();
    return () => {
      stopStream();
    };
  }, [open, startCamera, stopStream]);

  useEffect(
    () => () => {
      // Belt-and-braces: on unmount ALWAYS release tracks.
      stopStream();
    },
    [stopStream]
  );

  const close = useCallback(() => {
    stopStream();
    onOpenChange(false);
  }, [onOpenChange, stopStream]);

  // ── OCR (lazy-loaded Tesseract.js) ─────────────────────────────
  const runOcr = useCallback(async (canvas) => {
    setStage("analyzing");
    setOcrError("");
    let worker = null;
    try {
      const { createWorker } = await import("tesseract.js");
      worker = await createWorker("eng", 1, { logger: () => {} });
      // Digits-only whitelist — sticker numbers are 1..650.
      await worker.setParameters({
        tessedit_char_whitelist: "0123456789",
      });
      const { data } = await worker.recognize(canvas);
      const text = (data?.text || "").replace(/\s+/g, "");
      // Extract the longest contiguous digit run, then constrain to 1..650.
      const runs = text.match(/\d+/g) || [];
      let candidate = "";
      for (const r of runs) {
        if (r.length > candidate.length) candidate = r;
      }
      const n = parseInt(candidate, 10);
      if (!Number.isFinite(n) || n < 1 || n > 650) {
        setOcrError("no_number");
        setNumber("");
      } else {
        setNumber(String(n));
      }
    } catch (err) {
      setOcrError("no_number");
      setNumber("");
    } finally {
      try {
        if (worker) await worker.terminate();
      } catch (_) {
        /* ignore */
      }
      setStage("confirm");
    }
  }, []);

  const capture = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const vw = video.videoWidth || 640;
    const vh = video.videoHeight || 480;
    canvas.width = vw;
    canvas.height = vh;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, vw, vh);
    stopStream(); // release camera immediately after capture
    await runOcr(canvas);
  }, [runOcr, stopStream]);

  // File-picker fallback (permission-denied path). Reads image into
  // canvas → OCR. File never leaves the device (no upload).
  const onFilePick = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = async () => {
        const canvas = canvasRef.current;
        if (!canvas) {
          URL.revokeObjectURL(url);
          return;
        }
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        await runOcr(canvas);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        setOcrError("no_number");
        setStage("confirm");
      };
      img.src = url;
    },
    [runOcr]
  );

  // ── Duplicate detection ───────────────────────────────────────────
  const dupsSet = useMemo(
    () => new Set(inv?.duplicates || []),
    [inv?.duplicates]
  );
  const missSet = useMemo(() => new Set(inv?.missing || []), [inv?.missing]);
  const parsedN = number ? parseInt(number, 10) : NaN;
  const numberValid =
    Number.isFinite(parsedN) && parsedN >= 1 && parsedN <= 650;
  const alreadyInTarget =
    numberValid &&
    ((target === "duplicates" && dupsSet.has(parsedN)) ||
      (target === "missing" && missSet.has(parsedN)));

  const tonalityKey =
    ocrError === "no_number" && !numberValid
      ? "figuscan.tonality_scan_error"
      : `figuscan.tonality_${grade}`;

  const submit = useCallback(async () => {
    if (!numberValid) return;
    setSubmitting(true);
    try {
      // Optimistic path — call toggle. If sticker is already present in
      // target list, back off with a translated toast (no double-add).
      if (alreadyInTarget) {
        toast.info(
          t("figuscan.already_added", "Sticker is already in your list.")
            .replace("{n}", String(parsedN))
            .replace(
              "{list}",
              target === "duplicates"
                ? t("nav.inventory", "Duplicates")
                : t("nav.inventory", "Missing")
            )
        );
        setSubmitting(false);
        return;
      }
      await toggle.mutateAsync({
        number: parsedN,
        list: target,
        grade,
        origin: "figuscan",
      });
      // Fire-and-forget audit event; failures don't block the UX.
      try {
        await api.post("/inventory/scan-event", {
          number: parsedN,
          grade,
          action:
            target === "duplicates" ? "added_duplicate" : "added_missing",
        });
      } catch (_) {
        /* ignore */
      }
      toast.success(
        `${
          target === "duplicates"
            ? t("figuscan.target_duplicate", "Add to Duplicates")
            : t("figuscan.target_missing", "Add to Missing")
        } #${parsedN} (${grade})`
      );
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  }, [numberValid, alreadyInTarget, toggle, parsedN, target, grade, close, t]);

  if (!open) return null;

  const gradeLabel = (code) =>
    t(`figuscan.grade_${code}_label`, code);

  const backdrop = (
    <div
      data-testid="figuscan-modal-backdrop"
      role="presentation"
      onClick={close}
      className="fixed inset-0 z-[1000] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("figuscan.modal_title", "Scan a Sticker")}
        data-testid="figuscan-modal"
        onClick={(e) => e.stopPropagation()}
        className={cardCx}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
          <h2
            data-testid="figuscan-title"
            className="font-display text-lg font-semibold text-white"
          >
            {t("figuscan.modal_title", "Scan a Sticker")}
          </h2>
          <button
            type="button"
            data-testid="figuscan-close"
            onClick={close}
            aria-label={t("figuscan.cancel", "Cancel")}
            className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-white/5 text-white/70 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Hidden canvas used for both capture + file-pick OCR pipelines. */}
          <canvas
            ref={canvasRef}
            data-testid="figuscan-canvas"
            style={{ display: "none" }}
          />

          {stage === "camera" ? (
            <div
              data-testid="figuscan-stage-camera"
              className="flex flex-col items-center gap-4"
            >
              {cameraError ? (
                <div
                  data-testid="figuscan-camera-fallback"
                  className="w-full rounded-md border border-white/10 bg-black/40 p-4 text-sm text-[#A1A1AA] font-body"
                >
                  <p>
                    {t(
                      "figuscan.upload_fallback",
                      "Camera unavailable. Upload a photo instead."
                    )}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    data-testid="figuscan-file-input"
                    onChange={onFilePick}
                    className="mt-3 block w-full text-sm text-white/80 file:mr-3 file:h-9 file:px-3 file:rounded-md file:border-0 file:bg-[#E5B83A] file:text-black file:font-semibold hover:file:opacity-90"
                  />
                </div>
              ) : (
                <>
                  <div className="relative w-full aspect-[4/3] rounded-md overflow-hidden bg-black">
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      data-testid="figuscan-video"
                      className="w-full h-full object-cover"
                    />
                    {/* Framing guide — gold dashed rect covering ~60% */}
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-[20%] border-2 border-dashed border-[#E5B83A]/80 rounded-md"
                    />
                  </div>
                  <p
                    data-testid="figuscan-frame-instruction"
                    className="text-xs text-[#A1A1AA] text-center font-body max-w-md"
                  >
                    {t(
                      "figuscan.frame_instruction",
                      "Frame the sticker with its number visible in the corner"
                    )}
                  </p>
                  <div className="flex items-center justify-center gap-3 w-full">
                    <button
                      type="button"
                      data-testid="figuscan-cancel"
                      onClick={close}
                      className={secondaryBtnCx}
                    >
                      {t("figuscan.cancel", "Cancel")}
                    </button>
                    <button
                      type="button"
                      data-testid="figuscan-capture"
                      onClick={capture}
                      className={primaryBtnCx}
                    >
                      <Camera className="h-4 w-4" />
                      {t("figuscan.capture", "Capture")}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : null}

          {stage === "analyzing" ? (
            <div
              data-testid="figuscan-stage-analyzing"
              className="flex flex-col items-center gap-3 py-10 text-white/80"
            >
              <Loader2 className="h-6 w-6 animate-spin text-[#E5B83A]" />
              <p className="text-sm font-body">
                {t("figuscan.analyzing", "Analyzing…")}
              </p>
            </div>
          ) : null}

          {stage === "confirm" ? (
            <div
              data-testid="figuscan-stage-confirm"
              className="space-y-4"
            >
              {ocrError === "no_number" ? (
                <p
                  data-testid="figuscan-ocr-error"
                  className="text-xs text-[#FFAAAA] font-body bg-[#3B0000]/30 border border-[#FF0000]/40 rounded-md px-3 py-2"
                >
                  {t(
                    "figuscan.error_no_number",
                    "Couldn't read the number clearly. Try better lighting or enter manually."
                  )}
                </p>
              ) : null}

              <div>
                <label
                  htmlFor="figuscan-number-input"
                  className="block text-xs font-medium text-white/80 mb-1.5 tracking-wide uppercase font-body"
                >
                  {t("figuscan.number_label", "Sticker Number (1–650)")}
                </label>
                <input
                  id="figuscan-number-input"
                  data-testid="figuscan-number-input"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={650}
                  step={1}
                  value={number}
                  placeholder="1–650"
                  onChange={(e) =>
                    setNumber(e.target.value.replace(/[^0-9]/g, ""))
                  }
                  className={inputCx}
                />
              </div>

              <div>
                <label
                  htmlFor="figuscan-grade-select"
                  className="block text-xs font-medium text-white/80 mb-1.5 tracking-wide uppercase font-body"
                >
                  {t("figuscan.grade_label", "Condition Grade")}
                </label>
                <select
                  id="figuscan-grade-select"
                  data-testid="figuscan-grade-select"
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  className={inputCx + " font-body pr-8 appearance-none"}
                >
                  {GRADES.map((code) => (
                    <option
                      key={code}
                      value={code}
                      className="bg-[#121212] text-white"
                    >
                      {gradeLabel(code)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Robo-Ball tonality box */}
              <div
                data-testid="figuscan-tonality"
                data-tonality-grade={
                  ocrError === "no_number" && !numberValid
                    ? "scan_error"
                    : grade
                }
                className="rounded-md border border-[#E5B83A]/40 bg-[#141005] px-4 py-3 text-sm text-white/90 font-body leading-relaxed"
              >
                {t(tonalityKey, "Ready to add?")}
              </div>

              {alreadyInTarget ? (
                <p
                  data-testid="figuscan-already-added"
                  className="text-xs text-[#FFB86B] font-body"
                >
                  {t("figuscan.already_added", "Already in your list.")
                    .replace("{n}", String(parsedN))
                    .replace(
                      "{list}",
                      target === "duplicates"
                        ? t(
                            "figuscan.target_duplicate",
                            "Duplicates"
                          )
                        : t("figuscan.target_missing", "Missing")
                    )}
                </p>
              ) : null}

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="figuscan-target-duplicates"
                  aria-pressed={target === "duplicates"}
                  onClick={() => setTarget("duplicates")}
                  className={
                    (target === "duplicates"
                      ? "bg-[#E5B83A]/15 border-[#E5B83A] text-[#E5B83A] "
                      : "bg-transparent border-white/15 text-white/70 ") +
                    "h-11 rounded-md border font-medium text-sm transition-colors hover:border-white/40"
                  }
                >
                  {t("figuscan.target_duplicate", "Add to Duplicates")}
                </button>
                <button
                  type="button"
                  data-testid="figuscan-target-missing"
                  aria-pressed={target === "missing"}
                  onClick={() => setTarget("missing")}
                  className={
                    (target === "missing"
                      ? "bg-[#E5B83A]/15 border-[#E5B83A] text-[#E5B83A] "
                      : "bg-transparent border-white/15 text-white/70 ") +
                    "h-11 rounded-md border font-medium text-sm transition-colors hover:border-white/40"
                  }
                >
                  {t("figuscan.target_missing", "Add to Missing")}
                </button>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  data-testid="figuscan-retake"
                  onClick={() => {
                    setNumber("");
                    setOcrError("");
                    setStage("camera");
                    startCamera();
                  }}
                  className={secondaryBtnCx + " flex-1"}
                >
                  <RefreshCcw className="h-4 w-4" />
                  {t("figuscan.retake", "Retake")}
                </button>
                <button
                  type="button"
                  data-testid="figuscan-confirm"
                  onClick={submit}
                  disabled={!numberValid || submitting}
                  className={primaryBtnCx + " flex-1"}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  {t("figuscan.confirm_button", "Add to Inventory")}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <footer className="px-5 py-3 border-t border-white/10 shrink-0">
          <p
            data-testid="figuscan-privacy-note"
            className="text-[11px] text-[#A1A1AA]/80 font-body flex items-center gap-1.5"
          >
            <Upload className="h-3 w-3 shrink-0" aria-hidden="true" />
            {t(
              "figuscan.privacy_note",
              "Your image stays on your device. Only the extracted number is saved."
            )}
          </p>
        </footer>
      </div>
    </div>
  );

  return createPortal(backdrop, document.body);
}
